import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { ProgressSpinnerComponent } from './widgets/progress-spinner/progress-spinner.component';
import { LoadingService } from './services/loading.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PopupType } from './widgets/app-popup/app-popup.component';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  AuthError,
  PopupRequest,
  AuthenticationResult,
} from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AccessDeniedPopupComponent } from './widgets/access-denied-popup/access-denied-popup.component';
import { CookieConsentComponent } from './widgets/cookie-consent/cookie-consent.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    MsalModule,
    RouterOutlet,
    ProgressSpinnerComponent,
    CookieConsentComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'UI';
  isIframe = false;
  loginDisplay = false;
  loggedinUserName: any;
  private readonly _destroying$ = new Subject<void>();
  isCustomReportRouteEnabled: boolean = false;
  // prettier-ignore
  private isMsalRedirectDetected: boolean = false; // NOSONAR - Assigned in constructor

  // prettier-ignore
  constructor( //NOSONAR
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
  ) {
    // Check for MSAL redirect parameters FIRST (they can be in search params OR hash)
    // MSAL redirect format: #code=...&state=... OR ?code=...&state=...
    const hash = window.location.hash;
    const search = window.location.search;
    const isMsalRedirect =
      hash.includes('code=') ||
      hash.includes('state=') ||
      hash.includes('error=') ||
      search.includes('code=') ||
      search.includes('state=') ||
      search.includes('error=');

    // Store in class property for ngOnInit to use
    this.isMsalRedirectDetected = isMsalRedirect;
    console.log('Constructor - MSAL redirect detected:', isMsalRedirect);

    // Check both regular query params and hash-based params for Veeva parameters
    // Angular routing may put params after hash: #/?param=value
    let searchString = window.location.search;
    if (!searchString && window.location.hash.includes('?')) {
      // Parameters are in the hash, extract them
      searchString = window.location.hash.substring(
        window.location.hash.indexOf('?'),
      );
      console.log('Constructor - Extracted params from hash:', searchString);
    }

    // prettier-ignore
    const urlParams = new URLSearchParams(searchString); //NOSONAR
    const sessionId = urlParams.get('sessionid');
    const projectId = urlParams.get('projectID');
    const userId = urlParams.get('userId');
    const source = urlParams.get('source');
    const clickedcustomreports = urlParams.get('clickedcustomreports');

    console.log('Constructor - Parsed params:', {
      sessionId,
      projectId,
      userId,
      source,
      clickedcustomreports,
    });

    // Check for existing session in THIS TAB's sessionStorage only
    // sessionStorage is tab-specific, so it distinguishes between:
    // - Page refresh (same tab, has sessionStorage) → ALLOW
    // - New tab URL paste (different tab, no sessionStorage) → BLOCK
    const hasStoredSession = sessionStorage.getItem('sessionId') !== null;

    // Check if this is a direct URL paste
    // Block if: no Veeva parameters AND not an MSAL redirect AND no valid stored session
    // If user has a valid session (from previous Veeva launch or refresh), allow access
    const isDirectAccess =
      !sessionId &&
      !projectId &&
      !userId &&
      !clickedcustomreports &&
      !source &&
      !isMsalRedirect &&
      !hasStoredSession;

    console.log('Constructor - Access validation:', {
      isDirectAccess,
      isMsalRedirect,
      hasStoredSession,
      hasVeevaParams: !!(
        sessionId ||
        projectId ||
        userId ||
        clickedcustomreports ||
        source
      ),
    });

    if (isDirectAccess) {
      // Direct URL access detected - no Veeva params, no MSAL redirect, no stored session
      console.log(
        'Direct URL access detected without Veeva parameters or stored session - blocking access',
      );
      // DO NOT clear stored data here - it would affect other tabs that are working
      // localStorage is shared across tabs, so clearing it would break existing sessions
      // Just set the access denied flag for THIS tab only (sessionStorage is tab-specific)
      sessionStorage.setItem('accessDenied', 'true');
      return;
    }

    // Log what type of access this is
    if (isMsalRedirect) {
      console.log(
        'MSAL authentication redirect detected - using stored Veeva parameters',
      );
      // Validate that we have stored session from initial Veeva launch
      if (!hasStoredSession) {
        console.log(
          'MSAL redirect but no stored session found - blocking access',
        );
        sessionStorage.setItem('accessDenied', 'true');
        return;
      }
      // Don't process Veeva params - they're already stored from initial launch
      // Don't clean URL here - MSAL needs the code parameter to complete authentication
    } else if (
      sessionId ||
      projectId ||
      userId ||
      clickedcustomreports ||
      source
    ) {
      // Initial Veeva launch - has URL parameters, process and store them
      console.log('Valid Veeva parameters detected - processing...');

      // session id logic for Sparci(to open in new tab)
      // prettier-ignore
      if (sessionId != null) { //NOSONAR
        sessionStorage.setItem('sessionId', sessionId);
      } else {
        sessionStorage.getItem('sessionId');
      }

      //to get project details
      // prettier-ignore
      sessionStorage.setItem('projectSource', source || '');
      sessionStorage.setItem('clickedProjectType', clickedcustomreports || '');

      // prettier-ignore
      if (projectId != null) { //NOSONAR
        localStorage.setItem('projectId', projectId);
      } else {
        // prettier-ignore
        if(clickedcustomreports === 'true' || source === 'tab'){ //NOSONAR
          localStorage.setItem('isSparci', 'false');
          localStorage.setItem('projectId', 'not_available');
        }else if(clickedcustomreports === 'false'){
          localStorage.setItem('isSparci', 'true');
          localStorage.getItem('projectId');
        }else {
          localStorage.getItem('projectId');
        }
        
      }

      //to get user id
      // prettier-ignore
      console.log('userIdValue', urlParams);
      // prettier-ignore
      if (userId != null) { //NOSONAR
        localStorage.setItem('userId', userId);
      } else {
        localStorage.getItem('userId');
      }

      // Set environment details from URL parameters
      this.envUrlDetails();
    } else if (hasStoredSession) {
      // Page refresh - no URL params, but has valid stored session
      console.log(
        'Page refresh detected - valid session exists, continuing with stored data',
      );
      // No need to process parameters - they're already in storage
      // Just continue to authentication flow
    }

    // Routing will be handled AFTER authentication completes in loginWithRedirect or loginWithPopUp
    // DO NOT call handleRouting() here - it causes premature routing before authentication

    //NOSONAR
    // localStorage.setItem('environment', 'dev');
    // localStorage.setItem('urlDomain', 'sb-unilever-claims.veevavault.com');
    // localStorage.setItem('vaultDomain', 'https://sb-unilever-claims.veevavault.com');
    // localStorage.setItem('environment', 'qa');
    // localStorage.setItem('urlDomain', 'sb-unilever-claims-qa.veevavault.com');
    // localStorage.setItem(
    //   'vaultDomain',
    //   'https://sb-unilever-claims-qa.veevavault.com'
    // );
  }

  ngOnInit() {
    // Subscribe to router events to update isCustomReportRouteEnabled dynamically
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroying$),
      )
      .subscribe((event: NavigationEnd) => {
        // Update the flag based on current URL
        this.isCustomReportRouteEnabled = event.url.includes('/customreports');
      });

    // Check initial route
    this.isCustomReportRouteEnabled =
      this.router.url.includes('/customreports');

    // Check if access was denied in constructor
    if (sessionStorage.getItem('accessDenied') === 'true') {
      console.log(
        'Access denied - preventing route loading and showing error popup',
      );
      // DO NOT clear stored data - it would affect other tabs that are working correctly
      // localStorage is shared across tabs, and other tabs may have valid sessions from Veeva

      // Navigate away from the current route to prevent components from loading
      // Use replaceUrl so user can't go back to the blocked route
      this.router.navigate(['access-denied'], { replaceUrl: true });

      // Clean up URL if it contains MSAL redirect parameters
      const currentUrl = window.location.href;
      if (
        currentUrl.includes('code=') ||
        currentUrl.includes('state=') ||
        currentUrl.includes('error=')
      ) {
        console.log('Cleaning MSAL redirect parameters from URL');
        // Use history API to clean URL without routing
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }

      // Show access denied error after URL cleanup
      this.showAccessDeneiedError();
      this.loadingService.setLoading(false);
      return;
    }

    this.loadingService.setLoading(true);

    // If this is an MSAL redirect, just set up the handler and let it process
    // Don't trigger new authentication
    if (this.isMsalRedirectDetected) {
      console.log(
        'ngOnInit - MSAL redirect detected, setting up redirect handler only',
      );
      this.setupMsalRedirectHandler();
      return;
    }

    // Get source parameter - check sessionStorage first (for MSAL redirect), then URL
    const urlParams = new URLSearchParams(window.location.search); //NOSONAR
    const urlSource = urlParams.get('source');
    const storedSource = sessionStorage.getItem('projectSource');
    const source = urlSource || storedSource;

    console.log('ngOnInit - source determination:', {
      urlSource,
      storedSource,
      finalSource: source,
    });

    // prettier-ignore
    this.isIframe = window !== window.parent && !window.opener; // NOSONAR // Remove this line to use Angular Universal

    if (source === 'tab') {
      console.log('login with popup for tab source');
      this.loginWithPopUp();
    } else {
      console.log('login with redirect for non-tab source');
      this.loginWithRedirect();
    }
    this.loadingService.setLoading(false);
  }

  /**
   * Setup MSAL redirect handler for processing authentication callback
   * This is called when returning from Microsoft authentication
   */
  setupMsalRedirectHandler() {
    console.log('Setting up MSAL redirect handler');
    this.authService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result) {
          console.log('MSAL redirect processed successfully', result);
          this.setLoginDisplay();
          this.checkAndSetActiveAccount();
          // Route to the appropriate page after successful authentication
          this.handleRouting();
        }
      },
      error: (error) => {
        console.error('MSAL redirect processing error', error);
        this.loadingService.setLoading(false);
      },
      complete: () => {
        console.log('MSAL redirect handling complete');
        this.loadingService.setLoading(false);
      },
    });
  }

  loginWithRedirect() {
    // Check if user is already authenticated before triggering new login
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      console.log('User already authenticated, skipping login redirect');
      this.setLoginDisplay();
      this.checkAndSetActiveAccount();
      this.handleRouting();
      this.loadingService.setLoading(false);
      return;
    }

    console.log('No existing authentication found, setting up login flow');
    this.authService.handleRedirectObservable().subscribe();
    // prettier-ignore
    this.isIframe = window !== window.parent && !window.opener; // NOSONAR // Remove this line to use Angular Universal

    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          // prettier-ignore
          window.location.pathname = '/'; //NOSONAR
        } else {
          this.setLoginDisplay();
        }
      });

    //to handle login errors
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
        ),
      )
      .subscribe((msg: EventMessage) => {
        const errorMessage = (msg.error as AuthError).errorMessage;
        if (errorMessage) {
          // Handle ERROR ServerError: access_denied: AADB2C90091: The user has cancelled entering self-asserted information
          // prettier-ignore
          if (errorMessage.indexOf('AADB2C90091') > -1) { //NOSONAR
            this.authService.instance.loginRedirect();
          }
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();

        // Check if user is authenticated before routing
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          console.log('User authenticated, proceeding with routing');
          // Route after authentication completes
          this.handleRouting();
        } else {
          console.log('User not authenticated, initiating login redirect');
          // User not authenticated, trigger login
          this.loginRedirect();
        }
      });

    if (
      sessionStorage.getItem('sessionId') == null ||
      localStorage.getItem('environment') == undefined
    ) {
      if (
        !localStorage.getItem('projectId') &&
        localStorage.getItem('projectId') !== 'not_available'
      ) {
        this.showAccessDeneiedError();
      }
    }
  }

  getSessionId() {
    const sessionId =
      '1B1807DC4D29F0C1894D59CC836B9133737125980D748B60C1972553440450D44D28465C947F55390E14F82F1396D31539F9212C9CA2C50A7152224873042222';
    sessionStorage.setItem('sessionId', sessionId);
  }

  getProjectId() {
    //prettier-ignore
    //NOSONAR
    let projectId = 'V2C00000000F002'; //NOSONAR
    //let projectId = 'V2C00000002O003'
    //NOSONAR
    //let projectId = 'V2C000000010001';
    //let projectId = 'V2C00000008P006'; // QA
    //let projectId = 'V2C00000004Z002';
    //let projectId = 'V2C00000004N001';
    //let projectId = 'V2C00000007N004'; // QA SPARCi Project
    //let projectId = 'V2C00000007M004'; // Saprci_2.0_test
    //let projectId = 'V2C000000021001'; //custom report
    //let projectId = 'V2C000000080004'; // QA SPARCi 2.0 Test ProjectS
    localStorage.setItem('projectId', projectId);
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    console.log('login display', this.loginDisplay);
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();
    console.log('active acc', activeAccount);
    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      console.log('acc info', accounts);
      console.log('active acc', activeAccount);
      this.loggedinUserName = accounts[0]?.name;
      sessionStorage.setItem(
        'loggedInUser',
        this.loggedinUserName?.split(',')[1],
      );
      sessionStorage.setItem('username', accounts[0].username);
      this.authService.instance.setActiveAccount(accounts[0]);
    } else {
      console.log('active acc', activeAccount);
      this.loggedinUserName = activeAccount?.name;
      sessionStorage.setItem(
        'loggedInUser',
        this.loggedinUserName?.split(',')[1],
      );
      sessionStorage.setItem('username', activeAccount?.username);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout(popup?: any) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  /**to handle routing based on URL parameters */
  handleRouting() {
    // After MSAL authentication, URL parameters are lost
    // Use sessionStorage values that were stored in constructor
    const pageSource = sessionStorage.getItem('projectSource');
    const clickedReportType = sessionStorage.getItem('clickedProjectType');

    // Try to get from URL first (for initial load), fallback to sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source') || pageSource;
    const clickedcustomreports =
      urlParams.get('clickedcustomreports') || clickedReportType;
    const pageDetails = urlParams.get('targetPage');

    console.log(
      'Routing - source:',
      source,
      'clickedcustomreports:',
      clickedcustomreports,
      'pageDetails:',
      pageDetails,
      'pageSource (sessionStorage):',
      pageSource,
      'clickedReportType (sessionStorage):',
      clickedReportType,
    );
    // Clear begin_here_ localStorage keys
    const allKeys = Object.keys(localStorage);
    const beginHereKeys = allKeys.filter((key) =>
      key.startsWith('begin_here_'),
    );
    beginHereKeys.forEach((key) => localStorage.removeItem(key));

    // Route based on source and clickedcustomreports parameters
    if (source === 'tab') {
      // Set additional localStorage items for tab source
      const projectID =
        urlParams.get('projectID') || localStorage.getItem('projectId');
      const userId = urlParams.get('userId') || localStorage.getItem('userId');
      const userName =
        urlParams.get('userName') || localStorage.getItem('userName');
      const vaultDomain =
        urlParams.get('vaultDomain') || localStorage.getItem('vaultDomain');
      const sessionId =
        urlParams.get('sessionid') || sessionStorage.getItem('sessionId');

      if (projectID) localStorage.setItem('projectID', projectID);
      if (userId) localStorage.setItem('userId', userId);
      if (userName) localStorage.setItem('userName', userName);
      if (vaultDomain) localStorage.setItem('vaultDomain', vaultDomain);
      if (sessionId) localStorage.setItem('sessionId', sessionId);

      console.log('Navigating to /customreports');
      this.router.navigate(['/customreports']);
    } else if (
      pageDetails === 'edit_claims' ||
      window.location.href.split('#')[1] === '/edit-claims-la' ||
      window.location.href.split('#')[1] === '/edit-la-confirm' ||
      window.location.href.split('#')[1] === '/edit-claims-confirm'
    ) {
      console.log('Navigating to /bulkentry/edit-claims-la');
      this.router.navigate(['/bulkentry/edit-claims-la']);
    } else if (clickedcustomreports === 'true') {
      console.log('Navigating to /customreports (custom reports)');
      this.router.navigate(['/customreports']);
    } else if (clickedcustomreports === 'false') {
      console.log('Navigating to /bulkentry/home (sparci project)');
      this.router.navigate(['/bulkentry/home']);
    } else {
      // Default fallback - if no routing criteria matched, assume sparci project
      console.log('No routing criteria matched, defaulting to /bulkentry/home');
      this.router.navigate(['/bulkentry/home']);
    }
  }

  /**to set target page */
  targetPage() {
    // prettier-ignore
    const targetPage = new URLSearchParams(window.location.search); //NOSONAR
    const pageDetails: any = targetPage.get('targetPage');
    const source = targetPage.get('source');
    const clickedcustomreports = targetPage.get('clickedcustomreports');
    // prettier-ignore
    console.log('route url', window.location.href.split('#')[1]); //NOSONAR

    const allKeys = Object.keys(localStorage);
    const beginHereKeys = allKeys.filter((key) =>
      key.startsWith('begin_here_'),
    );
    beginHereKeys.forEach((key) => localStorage.removeItem(key));

    // prettier-ignore
    if(source === 'tab')
       {
          console.log('clickedcustomreports source', clickedcustomreports);
          console.log('source ', source)

          localStorage.setItem('projectID',targetPage['projectID']);
          localStorage.setItem('userId',targetPage['userId']);
          localStorage.setItem('userName',targetPage['userName']);
          localStorage.setItem('vaultDomain',targetPage['vaultDomain']);
          localStorage.setItem('sessionId',targetPage['sessionid']);
           this.router.navigate(['/customreports']);
           //NOSONAR
          // this.loginWithPopUp(); 
    
    } else if (
        pageDetails == 'edit_claims' ||
        window.location.href.split('#')[1] == '/edit-claims-la' || //NOSONAR
        window.location.href.split('#')[1] == '/edit-la-confirm' || //NOSONAR
        window.location.href.split('#')[1] == '/edit-claims-confirm' //NOSONAR
      ) {
        // add route of confirmation pages of LA-claim
        this.router.navigate(['/bulkentry/edit-claims-la']);
      } else if (clickedcustomreports === 'true') {
        console.log('clickedcustomreports custom report', clickedcustomreports)
         console.log('source custom', source)
        this.router.navigate(['/customreports']);
      } else if (clickedcustomreports === 'false') {
         console.log('clickedcustomreports home', clickedcustomreports)
        console.log('source home', source)
         this.router.navigate(['/bulkentry/home']);
      }
  }

  loginWithPopUp() {
    console.log('start login with popup');
    const activeAccount = this.authService.instance.getActiveAccount();
    // prettier-ignore
    if (!activeAccount) { //NOSONAR
      console.log('start login with popup not active account');
      const PopupRequest: PopupRequest = {
        scopes: ['user.read'],
      };
      this.authService.initialize().subscribe(() => {
        this.authService
          .loginPopup(PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
            console.log('start login with popup not active account success');
            // Set logged in user details
            this.loggedinUserName = response.account?.name;
            sessionStorage.setItem(
              'loggedInUser',
              this.loggedinUserName?.split(',')[1],
            );
            sessionStorage.setItem(
              'username',
              response.account?.username || '',
            );
            // Route after authentication completes
            this.handleRouting();
          });
      });
    } else {
      console.log('User already authenticated in popup flow');
      this.loggedinUserName = activeAccount?.name;
      sessionStorage.setItem(
        'loggedInUser',
        this.loggedinUserName?.split(',')[1],
      );
      sessionStorage.setItem('username', activeAccount?.username);
      // Route after setting account details
      this.handleRouting();
    }
  }

  /**to get url details*/
  envUrlDetails() {
    // prettier-ignore
    const envParams = new URLSearchParams(window.location.search); //NOSONAR
    const env: any = envParams.get('env');
    // prettier-ignore
    if (localStorage.getItem('urlDomain') != null) { //NOSONAR
      localStorage.getItem('urlDomain');
    } else {
      localStorage.setItem('urlDomain', env);
    }

    let environment;
    if (
      localStorage.getItem('urlDomain') == 'sb-unilever-claims.veevavault.com'
    ) {
      localStorage.setItem('environment', 'dev');
    } else if (
      localStorage.getItem('urlDomain') ==
      'sb-unilever-claims-sandbox--limited-release.veevavault.com'
    ) {
      localStorage.setItem('environment', 'sandbox');
    } else if (
      localStorage.getItem('urlDomain') ==
      'sb-unilever-claims-qa.veevavault.com'
    ) {
      localStorage.setItem('environment', 'qa');
    } else if (
      localStorage.getItem('urlDomain') ==
      'sb-unilever-claims-training.veevavault.com'
    ) {
      localStorage.setItem('environment', 'trn');
    } else if (
      localStorage.getItem('urlDomain') == 'unilever-bbfclaims.veevavault.com'
    ) {
      localStorage.setItem('environment', 'prod');
    }

    // prettier-ignore
    if (window?.location?.href?.includes('customreports')) { //NOSONAR
      this.isCustomReportRouteEnabled = true;
    } else {
      this.isCustomReportRouteEnabled = false;
    }
  }

  // to show access denied popup when user trying Sparci URL directly
  showAccessDeneiedError() {
    console.log(
      'You will be able to access SPARCi only via a Project in Veeva Claims.',
    );
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.height = '260px';
    dialogConfig.width = '345px';
    dialogConfig.data = {
      icon: '../../../assets/images/icons/access-denied.png',
      method: 'redirect',
      message:
        'You will be able to access SPARCi only via a Project in Veeva Claims.',
      type: PopupType.ERROR,
    } as any;
    this.dialog
      .open(AccessDeniedPopupComponent, dialogConfig)
      .afterClosed()
      .subscribe((response) => {});
  }

  // Clear all stored data to prevent unauthorized access with old credentials
  clearAllStoredData() {
    console.log('Clearing all localStorage and sessionStorage data');

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Also logout from MSAL if user is authenticated
    try {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        console.log('User authenticated in MSAL - logging out silently');
        // Set active account to null to clear authentication state
        this.authService.instance.setActiveAccount(null);
      }
    } catch (error) {
      console.error('Error clearing MSAL authentication:', error);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
